import { useState, useMemo, useEffect, useRef } from "react";

const useFocusDropdown = ({
  options,
  multiSelect = false,
  onSelectionChange,
  initialSelection = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedItems, setSelectedItems] = useState(initialSelection);
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) {
      return options.filter(
        (option) =>
          !selectedItems.some((selected) => selected.id === option.id),
      );
    }
    return options.filter(
      (option) =>
        option.text.toLowerCase().includes(inputValue.toLowerCase()) &&
        !selectedItems.some((selected) => selected.id === option.id),
    );
  }, [options, inputValue, selectedItems]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setInputValue("");
        setHighlightedIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleContainerMouseDown = (e) => {
    if (e.target !== inputRef.current) {
      e.preventDefault();
    }
  };

  const handleContainerClick = (e) => {
    if (e.target === inputRef.current) return;
    if (
      e.target.closest &&
      (e.target.closest(".ReactTags__remove") ||
        e.target.closest(".select2-results"))
    )
      return;

    if (isOpen) {
      setIsOpen(false);
      setHighlightedIndex(null);
    } else {
      setIsOpen(true);
      setHighlightedIndex(null);
      inputRef.current?.focus();
    }
  };

  const handleInputFocus = () => {
    if (!isOpen) {
      setIsOpen(true);
      setHighlightedIndex(null);
    }
  };

  const handleInputBlur = () => {
    setIsOpen(false);
    setInputValue("");
    setHighlightedIndex(null);
  };

  const handleInputChange = (value) => {
    setInputValue(value);
    if (!isOpen) setIsOpen(true);
  };

  const selectOption = (option) => {
    if (multiSelect) {
      const newSelection = [...selectedItems, option];
      setSelectedItems(newSelection);
      onSelectionChange?.(newSelection);
    } else {
      setSelectedItems([option]);
      onSelectionChange?.([option]);
      setIsOpen(false);
    }
    setInputValue("");
    setHighlightedIndex(null);
  };

  const removeItem = (index) => {
    const newSelection = selectedItems.filter((_, i) => i !== index);
    setSelectedItems(newSelection);
    onSelectionChange?.(newSelection);
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 40 || e.keyCode === 38) {
      e.preventDefault();
      const optionsLength = filteredOptions.length;
      if (optionsLength === 0) return;

      let newIndex = highlightedIndex;
      if (e.keyCode === 38) {
        newIndex =
          highlightedIndex === null || highlightedIndex === 0
            ? optionsLength - 1
            : highlightedIndex - 1;
      } else {
        newIndex =
          highlightedIndex === null || highlightedIndex === optionsLength - 1
            ? 0
            : highlightedIndex + 1;
      }
      setHighlightedIndex(newIndex);
    } else if (e.keyCode === 13) {
      e.preventDefault();
      if (highlightedIndex !== null && filteredOptions[highlightedIndex]) {
        selectOption(filteredOptions[highlightedIndex]);
      }
    } else if (
      e.keyCode === 8 &&
      !inputValue.trim() &&
      selectedItems.length > 0 &&
      multiSelect
    ) {
      removeItem(selectedItems.length - 1);
    } else if (e.keyCode === 27) {
      setIsOpen(false);
      setHighlightedIndex(null);
    }
  };

  return {
    isOpen,
    inputValue,
    selectedItems,
    setSelectedItems,
    highlightedIndex,
    setHighlightedIndex,
    filteredOptions,
    inputRef,
    containerRef,
    handleContainerMouseDown,
    handleContainerClick,
    handleInputFocus,
    handleInputBlur,
    handleInputChange,
    handleKeyDown,
    selectOption,
    removeItem,
  };
};

export default useFocusDropdown;
